import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useSelector } from 'react-redux'

import {
  recipeFetcher,
  standardRecipeTileFetcher,
  type RecipeCustomisations,
  type RecipeQueryResponse,
} from '@library/api-menu-experience'
import type { CollectionUUID, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasket } from 'routes/Menu/domains/basket'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import { useRecipeGridItemOverrides } from './useRecipeGridItemOverrides'

type IngredientCustomisations = Extract<RecipeCustomisations, { type: 'ingredients' }>
type CustomisableRecipe = RecipeQueryResponse & { customisations: IngredientCustomisations }

export type RecipeGridCustomisationData = {
  customisations?: IngredientCustomisations
  customisationRecipe?: RecipeQueryResponse
  collectionId?: CollectionUUID
  menuId?: string | null
  selectedRecipeId?: RecipeUUID
  isLoadingCustomisations: boolean
  hasCustomisationError: boolean
  isCustomisationSelectionPending?: boolean
  hasCustomisationSelectionError?: boolean
  hasOpenedCustomisations: boolean
}

type RecipeGridCustomisationSelection = {
  recipeId: RecipeUUID
}

type OpenPanel = {
  originalRecipeId: RecipeUUID
  selectedRecipeId: RecipeUUID
  numPortions: number
  recipePosition?: number
}

type LoadedPanel = OpenPanel & {
  recipe: CustomisableRecipe
  failedRecipeId?: RecipeUUID
}

type PanelState =
  | { status: 'closed' }
  | (OpenPanel & { status: 'loading' })
  | (OpenPanel & { status: 'error' })
  | (LoadedPanel & { status: 'ready' })
  | (LoadedPanel & { status: 'selecting' })

type UseRecipeGridCustomisationDataArgs = {
  collectionId?: string
  menuId: string
}

type TrackRecipeAlternativeOptionsMenuOpen = {
  action: 'recipe-alternative-options-menu-open'
  recipeId: RecipeUUID
  collectionId: string
}

type TrackClickCustomise = {
  action: 'click_customise'
  properties: {
    collection_id: string
    recipe_id: RecipeUUID
    recipe_uuid: RecipeUUID
    menu_uuid: string | null
    event_screen: 'view_recipe_list_screen'
    recipe_list_position?: number
  }
}

const idleCustomisationData: RecipeGridCustomisationData = {
  isLoadingCustomisations: false,
  hasCustomisationError: false,
  hasOpenedCustomisations: false,
}

const hasIngredientCustomisationOptions = (
  recipe: RecipeQueryResponse,
): recipe is CustomisableRecipe =>
  recipe.customisations.type === 'ingredients' &&
  recipe.customisations.slots.some((slot) => slot.values.length > 1)

const getOpenRecipeId = (panel: PanelState) =>
  panel.status === 'closed' ? null : panel.originalRecipeId

export const useRecipeGridCustomisationData = ({
  collectionId,
  menuId,
}: UseRecipeGridCustomisationDataArgs) => {
  const [panel, setPanel] = useState<PanelState>({ status: 'closed' })
  const panelRequestIdRef = useRef(0)
  const contextKeyRef = useRef('')
  const detailsCacheRef = useRef(new Map<string, Promise<RecipeQueryResponse>>())
  const { getNumPortionsForRecipeId, getQuantitiesForRecipeId } = useBasket()
  const deliveryDate = useSelector(getBasketDate)
  const numPortions = useSelector(getNumPortions)
  const contextKey = `${menuId}:${deliveryDate}:${numPortions}:${collectionId ?? ''}`
  const overrideContextKey = `${menuId}:${deliveryDate}`
  const handleOverrideRemoved = useCallback((recipeId: RecipeUUID) => {
    setPanel((current) => {
      if (current.status === 'closed' || current.selectedRecipeId !== recipeId) {
        return current
      }

      panelRequestIdRef.current += 1

      return { status: 'closed' }
    })
  }, [])
  const {
    applyOverride,
    getRecipeGridItem,
    getSelectedRecipeId,
    reconcileAfterRemoval: reconcileCustomisedRecipeGridItemAfterRemoval,
    refreshOverride: refreshCustomisedRecipeGridItem,
  } = useRecipeGridItemOverrides({
    boxNumPortions: numPortions,
    contextKey: overrideContextKey,
    deliveryDate,
    menuId,
    getNumPortionsForRecipeId,
    getQuantitiesForRecipeId,
    onOverrideRemoved: handleOverrideRemoved,
  })

  contextKeyRef.current = contextKey

  useEffect(() => {
    panelRequestIdRef.current += 1
    detailsCacheRef.current.clear()
    setPanel({ status: 'closed' })
  }, [contextKey])

  const isCurrentPanelRequest = useCallback(
    (requestId: number, requestContextKey: string) =>
      panelRequestIdRef.current === requestId && contextKeyRef.current === requestContextKey,
    [],
  )

  const fetchRecipeDetails = useCallback(
    (recipeId: RecipeUUID, portions: number) => {
      const cacheKey = `${contextKey}:${recipeId}:${portions}`
      const cachedRequest = detailsCacheRef.current.get(cacheKey)

      if (cachedRequest) {
        return cachedRequest
      }

      const request: Promise<RecipeQueryResponse> = recipeFetcher({
        menuId,
        recipeId,
        numPortions: portions,
        deliveryDate,
        collectionId,
      }).finally(() => {
        if (detailsCacheRef.current.get(cacheKey) === request) {
          detailsCacheRef.current.delete(cacheKey)
        }
      })

      detailsCacheRef.current.set(cacheKey, request)

      return request
    },
    [collectionId, contextKey, deliveryDate, menuId],
  )

  const closeRecipeCustomisations = useCallback(() => {
    panelRequestIdRef.current += 1
    setPanel({ status: 'closed' })
  }, [])

  const openRecipeCustomisations = useCallback(
    async (originalRecipeId: RecipeUUID, recipePosition?: number) => {
      const selectedRecipeId = getSelectedRecipeId(originalRecipeId)
      const selectedNumPortions = getNumPortionsForRecipeId(selectedRecipeId)
      panelRequestIdRef.current += 1
      const requestId = panelRequestIdRef.current
      const requestContextKey = contextKey

      setPanel({
        status: 'loading',
        originalRecipeId,
        selectedRecipeId,
        numPortions: selectedNumPortions,
        recipePosition,
      })

      if (collectionId) {
        queueTrackingEvent<TrackRecipeAlternativeOptionsMenuOpen>({
          action: 'recipe-alternative-options-menu-open',
          recipeId: selectedRecipeId,
          collectionId,
        })
        queueTrackingEvent<TrackClickCustomise>({
          action: 'click_customise',
          properties: {
            collection_id: collectionId,
            recipe_id: selectedRecipeId,
            recipe_uuid: selectedRecipeId,
            menu_uuid: menuId,
            event_screen: 'view_recipe_list_screen',
            recipe_list_position: recipePosition,
          },
        })
      }

      try {
        const recipe = await fetchRecipeDetails(selectedRecipeId, selectedNumPortions)

        if (!isCurrentPanelRequest(requestId, requestContextKey)) {
          return
        }

        if (!hasIngredientCustomisationOptions(recipe)) {
          setPanel({
            status: 'error',
            originalRecipeId,
            selectedRecipeId,
            numPortions: selectedNumPortions,
            recipePosition,
          })

          return
        }

        setPanel({
          status: 'ready',
          originalRecipeId,
          selectedRecipeId,
          numPortions: selectedNumPortions,
          recipePosition,
          recipe,
        })
      } catch {
        if (isCurrentPanelRequest(requestId, requestContextKey)) {
          setPanel({
            status: 'error',
            originalRecipeId,
            selectedRecipeId,
            numPortions: selectedNumPortions,
            recipePosition,
          })
        }
      }
    },
    [
      collectionId,
      contextKey,
      fetchRecipeDetails,
      getNumPortionsForRecipeId,
      isCurrentPanelRequest,
      menuId,
      getSelectedRecipeId,
    ],
  )

  const selectRecipeCustomisation = useCallback(
    async (
      originalRecipeId: RecipeUUID,
      selection: RecipeGridCustomisationSelection,
      recipePosition?: number,
    ) => {
      if (
        panel.status !== 'ready' ||
        panel.originalRecipeId !== originalRecipeId ||
        !menuId ||
        !deliveryDate
      ) {
        return
      }

      const previousPanel = panel
      const selectedNumPortions = getNumPortionsForRecipeId(selection.recipeId)
      panelRequestIdRef.current += 1
      const requestId = panelRequestIdRef.current
      const requestContextKey = contextKey

      setPanel({
        ...previousPanel,
        status: 'selecting',
        failedRecipeId: undefined,
      })

      try {
        const [recipe, recipeTile] = await Promise.all([
          fetchRecipeDetails(selection.recipeId, selectedNumPortions),
          standardRecipeTileFetcher({
            recipeId: selection.recipeId,
            menuId,
            numPortions: selectedNumPortions,
            deliveryDate,
          }),
        ])

        if (!isCurrentPanelRequest(requestId, requestContextKey)) {
          return
        }

        if (!hasIngredientCustomisationOptions(recipe)) {
          throw new Error('Selected recipe does not contain ingredient customisations')
        }

        applyOverride(originalRecipeId, recipeTile, selectedNumPortions)
        setPanel({
          status: 'ready',
          originalRecipeId,
          selectedRecipeId: recipeTile.id,
          numPortions: selectedNumPortions,
          recipePosition,
          recipe,
        })
      } catch {
        datadogRum.addAction('menu_experience_recipe_customisation_selection_failed', {
          menu_id: menuId,
          original_recipe_id: originalRecipeId,
          recipe_id: selection.recipeId,
          num_portions: selectedNumPortions,
        })

        if (isCurrentPanelRequest(requestId, requestContextKey)) {
          setPanel({
            ...previousPanel,
            status: 'ready',
            failedRecipeId: selection.recipeId,
          })
        }
      }
    },
    [
      contextKey,
      deliveryDate,
      applyOverride,
      fetchRecipeDetails,
      getNumPortionsForRecipeId,
      isCurrentPanelRequest,
      menuId,
      panel,
    ],
  )

  useEffect(() => {
    if (panel.status !== 'ready') {
      return
    }

    const currentNumPortions = getNumPortionsForRecipeId(panel.selectedRecipeId)

    if (currentNumPortions === panel.numPortions) {
      return
    }

    const previousPanel = panel
    panelRequestIdRef.current += 1
    const requestId = panelRequestIdRef.current
    const requestContextKey = contextKey

    setPanel({ ...panel, status: 'selecting' })
    void fetchRecipeDetails(panel.selectedRecipeId, currentNumPortions)
      .then((recipe) => {
        if (!isCurrentPanelRequest(requestId, requestContextKey)) {
          return
        }

        setPanel(
          hasIngredientCustomisationOptions(recipe)
            ? { ...previousPanel, recipe, numPortions: currentNumPortions }
            : {
                status: 'error',
                originalRecipeId: panel.originalRecipeId,
                selectedRecipeId: panel.selectedRecipeId,
                numPortions: currentNumPortions,
                recipePosition: panel.recipePosition,
              },
        )
      })
      .catch(() => {
        if (isCurrentPanelRequest(requestId, requestContextKey)) {
          setPanel({
            status: 'error',
            originalRecipeId: panel.originalRecipeId,
            selectedRecipeId: panel.selectedRecipeId,
            numPortions: currentNumPortions,
            recipePosition: panel.recipePosition,
          })
        }
      })
  }, [contextKey, fetchRecipeDetails, getNumPortionsForRecipeId, isCurrentPanelRequest, panel])

  const retryRecipeCustomisation = useCallback(() => {
    if (panel.status !== 'ready' || !panel.failedRecipeId) {
      return
    }

    void selectRecipeCustomisation(
      panel.originalRecipeId,
      { recipeId: panel.failedRecipeId },
      panel.recipePosition,
    )
  }, [panel, selectRecipeCustomisation])

  const activeRecipeId = getOpenRecipeId(panel)

  const activeRecipeState = useMemo<RecipeGridCustomisationData>(() => {
    if (panel.status === 'closed') {
      return idleCustomisationData
    }

    if (panel.status === 'loading') {
      return {
        isLoadingCustomisations: true,
        hasCustomisationError: false,
        hasOpenedCustomisations: true,
      }
    }

    if (panel.status === 'error') {
      return {
        isLoadingCustomisations: false,
        hasCustomisationError: true,
        hasOpenedCustomisations: true,
      }
    }

    return {
      customisations: panel.recipe.customisations,
      customisationRecipe: panel.recipe,
      collectionId: collectionId as CollectionUUID | undefined,
      menuId,
      selectedRecipeId: panel.selectedRecipeId,
      isLoadingCustomisations: false,
      hasCustomisationError: false,
      isCustomisationSelectionPending: panel.status === 'selecting',
      hasCustomisationSelectionError: Boolean(panel.failedRecipeId),
      hasOpenedCustomisations: true,
    }
  }, [collectionId, menuId, panel])

  const getRecipeCustomisationData = useCallback(
    (recipeId: RecipeUUID): RecipeGridCustomisationData =>
      recipeId === activeRecipeId ? activeRecipeState : idleCustomisationData,
    [activeRecipeId, activeRecipeState],
  )

  return {
    activeRecipeId,
    getRecipeGridItem,
    getRecipeCustomisationData,
    openRecipeCustomisations,
    closeRecipeCustomisations,
    selectRecipeCustomisation,
    retryRecipeCustomisation,
    refreshCustomisedRecipeGridItem,
    reconcileCustomisedRecipeGridItemAfterRemoval,
  }
}
